import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  THEMES,
  THEME_ORDER,
  DEFAULT_THEME,
  THEME_STORAGE_KEY,
  getTheme,
  getNextTheme,
  isDarkTheme,
  getChartColors,
  getStatusColor,
} from '../constants/theme';

/**
 * Theme Context
 *
 * Provides theme state and utilities to the entire application.
 * Supports 4 themes: Dark, Light, High Contrast, Industrial
 *
 * Usage:
 * const { theme, themeId, setTheme, cycleTheme, colors, isDark } = useTheme();
 */

const ThemeContext = createContext(null);

// App-wide font scale (a "display density" control, not per-panel) -- lets a
// user on a large/high-res monitor shrink the whole UI instead of every
// MUI component (which sizes off rem, cascading from the html root
// font-size) eating excess screen real estate. Persisted per-browser like
// the theme choice, so it's still there next time they log in on this
// machine.
export const FONT_SCALE_STORAGE_KEY = 'fast_font_scale';
const DEFAULT_FONT_SCALE = 1;
export const FONT_SCALE_OPTIONS = [
  { id: 'small', label: 'Small', scale: 0.875 },
  { id: 'medium', label: 'Medium (Default)', scale: 1 },
  { id: 'large', label: 'Large', scale: 1.125 },
  { id: 'xlarge', label: 'Extra Large', scale: 1.25 },
];

export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default
  const [themeId, setThemeId] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && THEMES[stored]) {
        return stored;
      }
    } catch (e) {
      console.warn('Failed to read theme from localStorage:', e);
    }
    return DEFAULT_THEME;
  });

  // Persist theme changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeId);
    } catch (e) {
      console.warn('Failed to save theme to localStorage:', e);
    }
  }, [themeId]);

  // Initialize font scale from localStorage or default
  const [fontScale, setFontScaleState] = useState(() => {
    try {
      const stored = parseFloat(localStorage.getItem(FONT_SCALE_STORAGE_KEY));
      if (!isNaN(stored) && stored > 0) {
        return stored;
      }
    } catch (e) {
      console.warn('Failed to read font scale from localStorage:', e);
    }
    return DEFAULT_FONT_SCALE;
  });

  // Persist font scale changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(FONT_SCALE_STORAGE_KEY, String(fontScale));
    } catch (e) {
      console.warn('Failed to save font scale to localStorage:', e);
    }
  }, [fontScale]);

  // Apply as the document root font-size -- MUI's default typeography is
  // rem-based, so this scales the entire app (text, spacing, icons that
  // size off font-size) without touching individual components.
  useEffect(() => {
    document.documentElement.style.fontSize = `${16 * fontScale}px`;
  }, [fontScale]);

  const setFontScale = useCallback((newScale) => {
    setFontScaleState(newScale);
  }, []);

  // Apply theme class to document for CSS variable usage
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', themeId);
    // Also set a class for dark/light mode detection
    document.documentElement.classList.remove('theme-dark', 'theme-light');
    document.documentElement.classList.add(isDarkTheme(themeId) ? 'theme-dark' : 'theme-light');
  }, [themeId]);

  // Get current theme object
  const theme = useMemo(() => getTheme(themeId), [themeId]);

  // Convenience accessor for colors
  const colors = useMemo(() => theme.colors, [theme]);

  // Check if current theme is dark variant
  const isDark = useMemo(() => isDarkTheme(themeId), [themeId]);

  // Set specific theme
  const setTheme = useCallback((newThemeId) => {
    if (THEMES[newThemeId]) {
      setThemeId(newThemeId);
    } else {
      console.warn(`Unknown theme: ${newThemeId}`);
    }
  }, []);

  // Cycle to next theme
  const cycleTheme = useCallback(() => {
    setThemeId(current => getNextTheme(current));
  }, []);

  // Toggle between dark and light (legacy support)
  const toggleDarkMode = useCallback(() => {
    setThemeId(current => {
      if (isDarkTheme(current)) {
        return 'light';
      }
      return 'dark';
    });
  }, []);

  // Get chart colors for current theme
  const chartColors = useMemo(() => getChartColors(themeId), [themeId]);

  // Get status color helper
  const getStatusColorForTheme = useCallback((status) => {
    return getStatusColor(status, colors);
  }, [colors]);

  // Available themes for UI selection
  const availableThemes = useMemo(() => {
    return THEME_ORDER.map(id => ({
      id,
      name: THEMES[id].name,
      description: THEMES[id].description,
      icon: THEMES[id].icon,
    }));
  }, []);

  const value = useMemo(() => ({
    // Current theme state
    themeId,
    theme,
    colors,
    isDark,

    // Theme actions
    setTheme,
    cycleTheme,
    toggleDarkMode,

    // Utilities
    chartColors,
    getStatusColor: getStatusColorForTheme,
    availableThemes,

    // Legacy support (for gradual migration)
    themeMode: isDark ? 'dark' : 'light',
    toggleTheme: toggleDarkMode,

    // Font scale (app-wide display size)
    fontScale,
    setFontScale,
    fontScaleOptions: FONT_SCALE_OPTIONS,
  }), [
    themeId,
    theme,
    colors,
    isDark,
    setTheme,
    cycleTheme,
    toggleDarkMode,
    chartColors,
    getStatusColorForTheme,
    availableThemes,
    fontScale,
    setFontScale,
  ]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 * @returns {object} Theme context value
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Hook to get just the colors (optimized for components that only need colors)
 * @returns {object} Theme colors
 */
export const useThemeColors = () => {
  const { colors } = useTheme();
  return colors;
};

/**
 * Hook to check if dark mode
 * @returns {boolean}
 */
export const useIsDark = () => {
  const { isDark } = useTheme();
  return isDark;
};

export default ThemeContext;
